import { Sections } from '@/types/sections.ts'
import type { Node } from '@deepcloud/api-deep-box'
import type { AxiosError } from 'axios'
import type { RouteLocation } from 'vue-router'

export const TASK_UPLOAD_STATUS = {
  ADDED: 'added',
  PROCESSING: 'processing',
  CANCELED: 'canceled',
  ERROR: 'error',
  WARNING: 'warning',
  COMPLETED: 'completed',
} as const

export type TaskUploadStatus =
  (typeof TASK_UPLOAD_STATUS)[keyof typeof TASK_UPLOAD_STATUS]

export type TaskUploadSection = Extract<Sections, 'inbox' | 'files'>

interface TaskUploadItem {
  node?: Node
  route?: RouteLocation
}

interface TaskUploadItemFileSize {
  total: number
  uploaded: number
}

export interface TaskUploadItemFile extends TaskUploadItem {
  file: File
  fileEntry: FileSystemFileEntry
  idFolder?: string | null
  uploadSize?: TaskUploadItemFileSize
}

interface TaskUploadItemFolder extends TaskUploadItem {
  name: string
  nodeId?: string
  parent?: string | null
  section?: string | null
}

type TaskUploadFolderTree = Record<string, TaskUploadItemFolder>

interface TaskUploadData extends TaskUploadItemFolder, TaskUploadItemFile {
  error?: AxiosError | Error
}

export type TaskUploadCallback = (
  taskUploadId: number,
  idOrIndex: string | number,
) => Promise<void>

export interface TaskUploadNode {
  id: string
  data?: Partial<TaskUploadData>
  status: TaskUploadStatus
  progress: number
  onSuccess?: TaskUploadCallback
  onFailure?: TaskUploadCallback
  abortController?: AbortController
}

export type TaskUploadTargetNode = Pick<Node, 'nodeId' | 'mimeType'>

interface TaskUploadTarget {
  typeId: string
  boxId: string
  node: TaskUploadTargetNode
  section: TaskUploadSection
}

export type TaskUpload<M = unknown> = {
  id: number
  folders?: Record<string, TaskUploadNode>
  files?: TaskUploadNode[]
  metadata: M
  target: TaskUploadTarget
  status: TaskUploadStatus
  progress: number
}

export type UploadNodes<M = unknown> = {
  folders?: TaskUploadFolderTree
  files?: TaskUploadItemFile[]
  metadata?: M
  target: TaskUploadTarget
  onSuccessFolder?: TaskUploadCallback
  onFailureFolder?: TaskUploadCallback
  onSuccessFile?: TaskUploadCallback
  onFailureFile?: TaskUploadCallback
}
